/**
 * English, keyed by the Thai that is written in the components.
 *
 * Scope on purpose: the screens somebody who does not read Thai needs in order to find a place, see what
 * is on, join it, and sign in — the shell, the home feed, venues, ตี้, the login sheet, the help pages.
 * The back office, the partner console and the account screens are not in here, so they keep speaking
 * Thai; adding them later is adding lines to this file, not touching components.
 *
 * What is deliberately NOT translated:
 *   · zone names (ยานนาวา, สุขุมวิท) — they are data, and they are what a map, a taxi driver and the
 *     venue's own signage say. "Yannawa" helps nobody standing on Sathu Pradit.
 *   · venue names, ตี้ titles, notes and captions — written by venues and members.
 *   · the playful room-name suggestions — jokes only work in the language they were made in.
 *
 * A word on vocabulary: ตี้ is the Thai word this community uses for one meetup, and DINKR's whole point
 * is that it is a social club, not a booking engine. "Session" is the closest honest English for it.
 */
export const EN: Record<string, string> = {
  // ---------- shell, navigation, generic actions ----------
  'หน้าแรก': 'Home',
  'หาตี้': 'Find a session',
  'สนาม': 'Venues',
  'คลับ': 'Clubs',
  'ฉัน': 'Me',
  'เมนูหลัก': 'Main menu',
  'แท็บหลัก': 'Main tabs',
  'DINKR หน้าแรก': 'DINKR home',
  'เข้าสู่ระบบ': 'Log in',
  'ค้นหา': 'Search',
  'ค้นหาคลับ #แท็ก สนาม ผู้เล่น': 'Search clubs, #tags, venues, players',
  'ค้นหาตี้': 'Search sessions',
  'ค้นหาสถานที่': 'Search places',
  'ทั้งหมด': 'All',
  'ดูทั้งหมด': 'See all',
  'ย้อนกลับ': 'Back',
  'กลับหน้าแรก': 'Back to home',
  'ยกเลิก': 'Cancel',
  'ลองใหม่': 'Try again',
  'ปิด': 'Close',
  'และ': 'and',
  'อื่น ๆ': 'Other',
  'ไม่ระบุ': 'Not specified',
  'กำลังโหลด': 'Loading',
  'กำลังหา…': 'Searching…',
  'กำลังส่ง…': 'Sending…',
  'กำลังทำ…': 'Working…',
  'กำลังลบ…': 'Deleting…',
  'กำลังกู้คืน…': 'Restoring…',
  'วิธีใช้': 'How it works',
  'วิธีใช้ DINKR Open Play': 'How DINKR Open Play works',
  'ข้อกำหนดการใช้งาน': 'Terms of use',
  'สารบัญ': 'Contents',
  'คำถามที่พบบ่อย': 'FAQ',
  'ข้อกำหนด': 'Terms',
  'ความเป็นส่วนตัว': 'Privacy',
  'นโยบายความเป็นส่วนตัว': 'Privacy policy',
  'รู้จัก DINKR': 'About DINKR',
  'สำรวจ DINKR': 'Explore DINKR',
  'ไม่พบหน้านี้ (404)': 'Page not found (404)',
  'ลิงก์อาจผิดหรือหน้าถูกลบไปแล้ว': 'The link may be wrong, or the page was removed.',

  'ข้ามไปเนื้อหา': 'Skip to content',
  'เข้าสู่ระบบก่อน': 'Log in first',
  'ใช้บัญชี LINE ของคุณ ไม่ต้องสมัครใหม่': 'Use your LINE account — no new sign-up.',
  'แจ้งเตือน': 'Notifications',
  'สลับธีม': 'Switch theme',
  'โหมดกลางคืน': 'Dark mode',
  'โหมดกลางวัน': 'Light mode',
  'ดูบนแผนที่': 'View on map',
  'แผนที่': 'Map',
  'รายการ': 'List',
  'เพิ่มเติม': 'More',
  'โหลดเพิ่ม': 'Load more',
  'ไม่สามารถโหลดข้อมูลได้': 'Could not load this',
  'เกิดข้อผิดพลาด': 'Something went wrong',
  'ลองโหลดใหม่': 'Reload',
  'ยังไม่มีข้อมูล': 'Nothing here yet',
  'ดูเพิ่ม': 'See more',
  'กลับ': 'Back',
  'ถัดไป': 'Next',
  'ก่อนหน้า': 'Previous',
  'บันทึก': 'Save',
  'ตกลง': 'OK',
  'ยืนยัน': 'Confirm',
  'แก้ไข': 'Edit',
  'ลบ': 'Delete',
  'แชร์': 'Share',
  'คัดลอกลิงก์แล้ว': 'Link copied',

  // ---------- home ----------
  'DINKR Open Play · นัดตีพิกเคิลบอล': 'DINKR Open Play · pickleball, badminton, tennis and more',
  'มาจอยตี้กับดิงเกอร์': 'Find a session with DINKR',
  'เรื่องราวจากคอมมูนิตี้': 'From the community',
  'ฟีดยังว่าง': 'Nothing here yet',
  'เริ่มบทสนทนา แชร์รูป หรือเล่าเรื่องจากคอร์ทได้เลย': 'Start the conversation — share a photo or a story from the court.',
  'เขียนโพสต์แรก': 'Write the first post',
  'เขียนโพสต์ถึงคอมมูนิตี้': 'Post to the community',
  'โพสต์ถึงคอมมูนิตี้': 'Post to the community',
  'มีอะไรอยากแชร์กับเพื่อน?': 'Anything you want to share?',
  'พิมพ์สิ่งที่อยากเล่าได้เลย ไม่ต้องเลือกสนาม': 'Just write — you do not have to pick a venue.',
  'โพสต์': 'Post',
  'ความเห็น': 'Comments',
  'กำลังเล่นอยู่': 'Playing now',
  'อีเว้นท์แนะนำ': 'Featured events',
  'อีเว้นท์แนะนำจากสนาม': 'Featured venue events',
  'เล่นวันนี้': 'Playing today',
  'หาคอร์ทสำหรับเกมถัดไป': 'Find a court for your next game',
  'สำรวจสนาม': 'Browse venues',
  'เจอแก๊งที่ชอบเล่นเหมือนกัน': 'Find people who play like you do',
  'เริ่มจากตี้ที่รับมือใหม่ เช็กข้อมูลสนาม แล้วทักทายเพื่อนร่วมคอร์ท':
    'Start with a beginner-friendly session, check the venue, and say hello on court.',
  'ครั้งแรกก็จอยได้': 'First time is fine',

  // ---------- sessions: list and filters ----------
  'ตี้': 'Sessions',
  'ตี้ที่กำลังจะมี': 'Upcoming sessions',
  'ตี้ที่จะมี': 'Upcoming',
  'ที่ผ่านมา': 'Past',
  'กำลังจะมี': 'Upcoming',
  'เลือกวัน': 'Pick a day',
  'เลือกวัน สไตล์ ระดับ หรือโซนที่สะดวก': 'Pick a day, a style, a level or an area that suits you.',
  'เลือกวันและระดับที่เหมาะกับคุณ': 'Pick a day and a level that suit you.',
  'สไตล์และระดับ': 'Style and level',
  'ระดับ': 'Level',
  'โซน': 'Area',
  'ทุกโซน': 'All areas',
  'ทุกระดับ': 'All levels',
  'ทุกวัน': 'Any day',
  'ใกล้ฉัน': 'Near me',
  'ใช้ตำแหน่งของฉัน': 'Use my location',
  'กิจกรรม': 'Activity',
  'ยังไม่มีตี้ตามเงื่อนไขนี้': 'No sessions match these filters',
  'ยังไม่มีตี้ที่ผ่านมา': 'No past sessions yet',
  'ลองเปลี่ยนวันหรือโซน หรือเป็นคนแรกที่เปิดตี้ — สนามด้านล่างคือที่ที่คนเล่นกันอยู่':
    'Try another day or area, or be the first to open one — the venues below are where people are playing.',
  'เปิดตี้': 'Open a session',
  'เปิดตี้ที่นี่': 'Open a session here',
  'เป็นคนแรกที่เปิดตี้ที่นี่': 'Be the first to open a session here',
  'ชวนวิ่ง แล้วปักหมุดเอง': 'Start a run and drop your own pin',

  // ---------- a session: status, roles, actions ----------
  'จบไปแล้ว · การ์ดอยู่ต่อไม่หาย ยังรีวิวเพื่อนร่วมตี้ได้':
    'Finished — the cards stay, and you can still review the people you played with.',
  'เปิดรับ': 'Open',
  'เต็ม': 'Full',
  'จบแล้ว': 'Finished',
  'ตี้จบแล้ว': 'Session finished',
  'ตี้นี้ถูกยกเลิก': 'This session was cancelled',
  'ตี้นี้ถูกลบแล้ว': 'This session was deleted',
  'ยังไม่เริ่ม': 'Not started',
  'เริ่มเล่นแล้ว': 'Playing now',
  'พร้อมลงคอร์ท': 'Ready to play',
  'วันนี้': 'Today',
  'พรุ่งนี้': 'Tomorrow',
  'จบไปเมื่อกี้': 'Just finished',
  'จบไปเมื่อวาน': 'Finished yesterday',
  'โฮสต์': 'Host',
  'โฮสต์:': 'Host:',
  'จัดโดย': 'Hosted by',
  'จัดโดยสนาม · อีเว้นท์อย่างเป็นทางการ': 'Hosted by the venue · official event',
  'ผู้เข้าร่วม': 'Players',
  'โฮสต์และผู้เข้าร่วม': 'Host and players',
  'ยังไม่มีใครเข้าร่วม': 'Nobody has joined yet',
  'มีคุณด้วย': 'including you',
  'คิวรอ': 'Waitlist',
  'รอคิว': 'On the waitlist',
  'เข้าร่วม': 'Join',
  'เข้าร่วมแล้ว': 'Joined',
  'เข้าร่วมตี้แล้ว เจอกันที่คอร์ท!': 'You are in — see you on court!',
  'ออกจากตี้': 'Leave session',
  'ออกจากตี้แล้ว': 'You left the session',
  'ออกจากคิวรอ': 'Leave waitlist',
  'ออกจากคิวแล้ว': 'You left the waitlist',
  'เข้าคิวรอแล้ว จะแจ้งเตือนเมื่อมีที่ว่าง': 'You are on the waitlist — we will tell you when a spot opens.',
  'ที่ว่างจะเลื่อนให้คนในคิวรอโดยอัตโนมัติ': 'A free spot goes to the waitlist automatically.',
  'ชวนเพื่อน': 'Invite a friend',
  'ชวนเพื่อนเข้าตี้': 'Invite friends to this session',
  '+ ชวน': '+ Invite',
  'เชิญแล้ว · รอตอบรับ': 'Invited · waiting for a reply',
  'ส่งคำเชิญแล้ว ยังไม่ได้เข้าร่วมตี้': 'Invitation sent — they have not joined yet.',
  'ราคา': 'Price',
  '/ คน': '/ person',
  'คอร์ท': 'courts', // the unit, as in "10 courts"; the venue page's own label is capitalised there
  'วัน': 'day',
  'คะแนน': 'Score',
  'รีวิว': 'Reviews',
  'รีวิวเพื่อนร่วมตี้ได้': 'You can review the people you played with',
  'รีวิวได้ตลอด ไม่มีหมดเวลา': 'Reviews stay open — there is no deadline.',
  'ส่งรีวิว': 'Submit review',
  'แก้รีวิวของคุณ': 'Edit your review',
  'บันทึกรีวิวแล้ว ขอบคุณ!': 'Review saved — thank you!',
  'ยังไม่มีรีวิว': 'No reviews yet',
  'รูปจากตี้นี้': 'Photos from this session',
  'ยังไม่มีรูปจากตี้นี้': 'No photos from this session yet',
  'ลงรูป': 'Add photos',
  'ลงรูปได้ตั้งแต่เริ่มเล่น': 'You can add photos from the moment play starts',
  'คุณลงรูปจากตี้นี้ได้ทุกเมื่อ ตั้งแต่เริ่มเล่นเป็นต้นไป': 'You can add photos any time from the start of play onwards.',
  'ผู้เข้าร่วมลงรูปได้ตั้งแต่เริ่มเล่นเป็นต้นไป ไม่มีหมดเวลา รูปจะขึ้นฟีดและบอร์ดของสนามด้วย':
    'Players can add photos from the start of play onwards, with no deadline. They also appear in the feed and on the venue board.',
  'ยกเลิกตี้': 'Cancel session',
  'ยกเลิกตี้นี้? ผู้เข้าร่วมทุกคนจะเห็นว่าตี้ถูกยกเลิก': 'Cancel this session? Everyone who joined will see that it was cancelled.',
  'ยกเลิกตี้แล้ว': 'Session cancelled',
  'ถ้าเปลี่ยนแผน ยกเลิกล่วงหน้าเพื่อให้ผู้เข้าร่วมวางแผนใหม่ได้': 'If plans change, cancel early so the others can rearrange.',
  'แก้ไขตี้': 'Edit session',
  'แก้ไขตี้ (คุณเป็นโฮสต์)': 'Edit session (you are the host)',
  'จัดการตี้ (โฮสต์)': 'Manage session (host)',
  'จัดการตี้ (แอดมิน)': 'Manage session (admin)',
  'ลบตี้': 'Delete session',
  'ลบตี้นี้?': 'Delete this session?',
  'ลบแล้วตี้จะหายจากทุกหน้า แต่กู้คืนได้ภายใน 48 ชั่วโมง':
    'It disappears from every page, and can be restored within 48 hours.',
  'ลบตี้แล้ว · กู้คืนได้ภายใน 48 ชั่วโมง': 'Session deleted · restorable for 48 hours',
  'ยืนยัน ลบตี้': 'Yes, delete it',
  'ไม่ลบ': 'Keep it',
  'กู้คืนตี้นี้': 'Restore this session',
  'กู้คืนตี้แล้ว': 'Session restored',
  'ข้อมูลยังเก็บไว้ — กดกู้คืนได้ภายใน 48 ชั่วโมง รูป คะแนน และรายชื่อกลับมาครบเหมือนเดิม':
    'Nothing was thrown away — restore within 48 hours and the photos, scores and player list all come back.',
  'เลยเวลากู้คืนแล้ว ข้อมูลยังอยู่ครบ แต่เอากลับมาเองไม่ได้':
    'The restore window has passed. The data is still there, but it can no longer be brought back from here.',
  'เพิ่มคนที่มาเล่น': 'Add a player who turned up',
  'เพิ่มคนที่มาเล่นแต่ไม่ได้กดเข้าร่วม': 'Add someone who played but never tapped join',
  'มีคนมาเล่นแต่ตกหล่น?': 'Somebody played but is missing?',
  'บันทึกว่าไม่ได้มา': 'Mark as no-show',
  'บันทึกว่าไม่ได้มา?': 'Mark as a no-show?',
  'ยืนยัน ไม่ได้มา': 'Yes, no-show',
  'เอาออกจากตี้': 'Remove from session',
  'เอาออกจากตี้?': 'Remove from this session?',
  'เอาออกจากคิวรอ?': 'Remove from the waitlist?',
  'ยืนยัน เอาออก': 'Yes, remove',
  'ย้อนกลับไม่ได้ แต่คนนั้นกดเข้าร่วมใหม่ได้ถ้าตี้ยังไม่จบ':
    'This cannot be undone, but they can join again while the session is still open.',
  'ตรวจรายชื่อก่อนเปิดรีวิว': 'Check the player list before reviews open',
  'ยืนยันรายชื่อ · เปิดให้รีวิว': 'Confirm list · open reviews',
  'ยืนยันรายชื่อตี้นี้? หลังจากนี้แก้ไขไม่ได้ และทุกคนในรายชื่อจะรีวิวกันได้':
    'Confirm the list? It cannot be edited afterwards, and everyone on it can review each other.',
  'ยืนยันรายชื่อแล้ว · เปิดให้รีวิวกันได้เลย': 'List confirmed · reviews are open',
  'ถ้าไม่กดยืนยัน ระบบจะเปิดรีวิวให้เองเมื่อครบ 48 ชั่วโมง': 'If you do not confirm, reviews open on their own after 48 hours.',
  'รอโฮสต์ยืนยันรายชื่อ': 'Waiting for the host to confirm the list',
  'ตี้นี้ขึ้นสถานะกำลังเล่นอยู่แล้ว 🟢': 'This session is now marked as playing 🟢',
  'ปิดสถานะเล่นอยู่แล้ว': 'Playing status turned off',
  'หน้าตี้ที่คุณเข้าร่วม': 'The sessions you joined',

  'ตี้นี้รับมือใหม่': 'Beginners welcome',
  'มาครั้งแรก': 'First time',
  'มาคนเดียวก็ได้ ไม่เคยเล่นก็ได้ — โฮสต์ตี้นี้เปิดรับมือใหม่': 'Come alone, come new — this host welcomes first-timers.',

  // ---------- venues ----------
  'ภาพรวม': 'Overview',
  'บอร์ด': 'Board',
  'ที่อยู่': 'Address',
  'โทร': 'Phone',
  'LINE / ติดต่อ': 'LINE / contact',
  'โซเชียล': 'Social',
  'เวลาเปิด': 'Opening hours',
  'สิ่งอำนวยความสะดวก': 'Facilities',
  'กีฬาที่เล่นได้': 'Sports played here',
  'รูปสนาม': 'Venue photos',
  'ภาพจากคอร์ท': 'From the court',
  'รูปจากคอร์ท': 'Photos from the court',
  'ในร่ม': 'Indoor',
  '· ในร่ม': '· indoor',
  '· กลางแจ้ง': '· outdoor',
  'เปิดใน Google Maps': 'Open in Google Maps',
  'พิกัดโดยประมาณ — ตรวจสอบก่อนเดินทาง': 'Approximate location — check before you travel',
  'เสนอสถานที่': 'Suggest a place',
  'เสนอสถานที่ใหม่': 'Suggest a new place',
  'เสนอรูปสนาม': 'Suggest a photo',
  'ลองเปลี่ยนโซนหรือคำค้น หรือเสนอสถานที่ที่คุณไปประจำให้เราเพิ่ม':
    'Try another area or search term, or suggest the place you play at.',
  'ติดตามสนาม': 'Follow venue',
  'ติดตามอยู่': 'Following',
  'ติดตามแล้ว · จะแจ้งเตือนเมื่อสนามลงอีเว้นท์': 'Following · we will tell you when this venue posts an event',
  'เลิกติดตามแล้ว': 'Unfollowed',
  'กดติดตามสนามไว้ จะได้แจ้งเตือนทันทีที่สนามลงอีเว้นท์': 'Follow the venue and you will hear as soon as it posts an event.',
  'ฉันคือเจ้าของสนามนี้': 'I own this venue',
  'คำขอเจ้าของสนามรอตรวจสอบ': 'Ownership claim under review',
  'สนามพาร์ทเนอร์ DINKR — เจ้าของสนามดูแลข้อมูลเอง': 'DINKR partner venue — kept up to date by the owner',
  'สนามนี้ยังไม่ได้เป็นพาร์ทเนอร์ — อีเว้นท์จากสนามจะขึ้นที่นี่เมื่อเจ้าของสนามเข้าร่วม':
    'This venue is not a partner yet — its events will appear here once the owner joins.',
  'จัดการสนาม': 'Manage venue',
  'จองสนาม': 'Book a court',
  'จองสนาม · เลือกสลอต': 'Book a court · pick a slot',
  'ดูการจอง': 'View booking',
  'จองคอร์ทผ่าน DINKR แล้ว': 'Booked through DINKR',
  'สมัคร / ดูรายละเอียดที่สนาม ↗': 'Sign up / details at the venue ↗',
  '(สมัครจริงที่ลิงก์ของสนาม)': '(you sign up on the venue’s own link)',
  'ดูตี้ที่สนามนี้': 'Sessions at this venue',
  'ดูตี้ทั้งหมด': 'See all sessions',
  'ยังไม่มีตี้ที่สนามนี้': 'No sessions at this venue yet',
  'คลับประจำสนาม': 'Clubs at this venue',
  'คลับอื่นที่มาที่นี่': 'Other clubs that play here',
  'ยังไม่มีคลับที่สนามนี้': 'No clubs at this venue yet',
  'สร้างคลับที่สนามนี้': 'Start a club here',
  'ก๊วนที่มาบ่อย': 'Regular groups',
  'ยังไม่ได้ตั้งเป็นคลับ': 'Not a club yet',
  'ตั้งเป็นคลับ': 'Make it a club',
  'ถ้าคุณมีก๊วนที่มาตีประจำ ตั้งเป็นคลับเพื่อให้คนอื่นเจอและขอเข้าร่วมได้':
    'If your group plays here regularly, make it a club so others can find it and ask to join.',
  'ยังไม่มีสถิติการมา': 'No attendance figures yet',
  'รีวิวสนามนี้': 'Review this venue',
  'คนที่เคยตีที่นี่รีวิวได้หลังตี้จบ': 'Anyone who has played here can review once their session is over.',
  'รีวิวได้เมื่อคุณเคยเข้าร่วมตี้ที่สนามนี้และตี้จบแล้ว':
    'You can review once you have joined a session here and it has finished.',
  'พื้น แสง ที่จอดรถ ราคา บรรยากาศ…': 'Floor, lighting, parking, price, atmosphere…',
  'บอร์ดยังว่าง': 'The board is empty',
  'เมื่อมีคนตีที่นี่แล้วลงรูป รูปจะมาอยู่ตรงนี้': 'When people play here and post photos, they land here.',
  'รูปจากตี้ที่สนามนี้ โพสต์โดยผู้เข้าร่วม': 'Photos from sessions at this venue, posted by the players',
  'รูปบนบอร์ดมาจากตี้จริง — ลงรูปได้จาก': 'Photos on the board come from real sessions — add yours from',
  'ยังไม่มีรูปสนามที่อนุมัติแล้ว': 'No approved venue photos yet',
  'รอบรั้วคอร์ท': 'Around the court',
  'ซ่อนแผนที่': 'Hide map',
  'เรียงตามความนิยม': 'Sorted by popularity',
  'เรียงตามระยะทางจากคุณ': 'Sorted by distance from you',
  'อีเว้นท์': 'Events',
  'อีเว้นท์จากสนาม': 'Venue events',
  'อีเว้นท์ทางการ': 'Official event',
  'ลงอีเว้นท์': 'Post an event',
  'โปสเตอร์อีเว้นท์': 'Event poster',
  'ยังไม่มีอีเว้นท์ที่จะมาถึง': 'No upcoming events',
  'ยังไม่มีอีเว้นท์ที่ผ่านมา': 'No past events',

  // ---------- clubs and players ----------
  'เข้าคลับ': 'Join club',
  'ขอเข้า': 'Ask to join',
  'ผู้เล่น': 'Players',
  'โปรไฟล์ของฉัน': 'My profile',
  'พาร์ทเนอร์': 'Partner',

  // ---------- levels, styles, people ----------
  'มือใหม่': 'Beginner',
  'กลาง': 'Intermediate',
  'โปร': 'Advanced',
  'ชิลล์': 'Chill',
  'จริงจัง': 'Competitive',
  'ชาย': 'Male',
  'หญิง': 'Female',
  'ต่ำกว่า 25': 'Under 25',
  '55 ขึ้นไป': '55+',
  'มือใหม่มารวมกันตรงนี้ ห้ามตบแรง': 'Beginners welcome — go easy on the smashes',
  'มือกลางที่ใจอยากเป็นโปร': 'Intermediate players with big ambitions',

  // ---------- "ฉัน": the profile page, its scores, and the account menu behind it ----------
  // Sentences built around a number are NOT in here. The dictionary is keyed by the Thai a component
  // writes, so a template literal reaches it as text it has never seen; and English wants "1 review"
  // where Thai is happy with "1 รีวิว" either way. Those ask isEn() and plural() where they are written.
  'โปรไฟล์สมาชิก': 'Member profile',
  'เมนูบัญชี': 'Account menu',
  'เมนูลัดบัญชี': 'Account shortcuts',
  'แก้โปรไฟล์': 'Edit profile',
  'ชวนเล่น': 'Invite to play',
  'ตี้ของฉัน': 'My sessions',
  'คลับของฉัน': 'My clubs',
  'ตั้งค่าบัญชี': 'Account settings',
  'เก็บโมเมนต์ดี ๆ จากคอร์ทไว้ที่นี่': 'Keep your good moments from the court here',
  'แชร์รูปกับคอมมูนิตี้': 'Share a photo with the community',
  'เกี่ยวกับผู้เล่น · ประวัติการเล่น': 'About this player · play history',
  'ประวัติการเล่น': 'Play history',
  'ยังไม่มีประวัติการเล่น': 'No play history yet',

  // the chameleon's stage names, which the server sends as data rather than as a key
  'ยังไม่ออกจากพุ่ม': 'Still in the bushes',
  'ออกมาดูโลก': 'Out having a look',
  'เริ่มคุ้นสนาม': 'Finding its way around',
  'ขาประจำ': 'A regular',
  'รู้จักกันทั้งสนาม': 'Known to the whole court',
  'ตัวจริงของย่าน': 'The real thing round here',

  // the level block
  'เลเวลผู้เล่น': 'Player level',

  // the scoreboard
  'คะแนนและประวัติรีวิว': 'Scores and review history',
  'คะแนนจากเพื่อนร่วมเล่น': 'Scores from the people you play with',
  'ความเก่ง': 'Skill',
  'ความสนุก': 'Fun',
  'คะแนนสะสม': 'points so far',
  'ยังไม่มีคะแนน หลังจบกิจกรรมเพื่อนร่วมเล่นสามารถให้คะแนนได้':
    'No scores yet — once a session is over, the people who were there can rate you.',
  'คะแนนแยกตามกิจกรรม ไม่นำมารวมกัน · เล่นกิจกรรมไหน คะแนนก็สะสมในกิจกรรมนั้น':
    'Scores are kept per activity and never added together — whatever you play, the points stay with that sport.',
  'ประวัติรีวิวที่ได้รับ': 'Reviews you have received',
  'ยังไม่มีรีวิว หลังจบกิจกรรมเพื่อนร่วมเล่นสามารถให้คะแนนได้':
    'No reviews yet — once a session is over, the people who were there can rate you.',
  'แสดง 10 ครั้งล่าสุด · คะแนนสะสมนับทุกครั้ง': 'Showing the last 10 · the totals count every one.',

  // the account menu
  'เพิ่มระดับและโซนที่เล่นภายหลังได้ เพื่อเตรียมเข้าร่วมตี้':
    'Add your level and the zone you play in whenever you like — it gets you ready to join a session.',
  'เพิ่มข้อมูล': 'Add details',
  'ตี้ถัดไป': 'Your next session',
  'ตี้ทั้งหมด': 'All sessions',
  'ยังไม่มีตี้ที่จะไป': 'Nothing on your calendar yet',
  'หาตี้ใกล้คุณหรือเปิดตี้เอง': 'Find a session near you, or open one yourself',
  'กำลังรอคิว': 'On the waitlist',
  'ตั้งคลับไหม?': 'Make it a club?',
  'ใหม่': 'New',
  'ดูก๊วนที่สนาม': 'See the crews at this venue',
  'การจองสนาม': 'Court bookings',
  'สนามของฉัน': 'My venues',
  'จัดการสนาม · ลงอีเว้นท์ · ทีมงาน': 'Manage venues · post events · staff',
  'พรีวิวตี้หลังจบเกม': 'Preview a finished session',
  'ทดลองให้คะแนนเพื่อนร่วมตี้': 'Try rating the people you played with',
  'หลังบ้าน DINKR': 'DINKR back office',

  // editing your profile
  'แก้ไขโปรไฟล์': 'Edit profile',
  'รูปปก': 'Cover photo',
  'เลือกรูปปก': 'Choose a cover photo',
  'รูปโปรไฟล์': 'Profile photo',
  'แสดงในตี้และคลับแทนชื่อ': 'Shown beside your name in sessions and clubs',
  'เลือกรูป': 'Choose a photo',
  'ชื่อที่แสดง': 'Display name',
  'a-z 0-9 _ ความยาว 3–20 · ใช้ในลิงก์โปรไฟล์ /@handle': 'a-z 0-9 _ , 3–20 characters · used in your profile link /@handle',
  'รูปแบบไม่ถูกต้อง': 'That format is not valid',
  'แนะนำตัว': 'About you',
  'สไตล์การเล่น เวลาที่สะดวก…': 'How you play, when you are free…',
  '— เลือก —': '— choose —',
  'จุดประสงค์': 'What you are after',
  'ได้ทั้งสองแบบ': 'Either is fine',
  'โซนที่เล่นบ่อย': 'Where you usually play',
  '— เลือกโซน —': '— choose a zone —',
  'กำลังบันทึก…': 'Saving…',
  'บันทึกโปรไฟล์แล้ว': 'Profile saved',

  // ---------- login ----------
  'เข้าสู่ระบบด้วย LINE': 'Log in with LINE',
  'เข้าร่วมตี้ เปิดตี้ และลงรูปได้เมื่อเข้าสู่ระบบ ใช้บัญชี LINE ของคุณ ไม่ต้องสมัครใหม่':
    'Log in to join sessions, open your own and post photos. Use your LINE account — no new sign-up.',
  'เปิดใน LINE เพื่อเข้าสู่ระบบ': 'Open in LINE to log in',
  'เข้าสู่ระบบผ่านเบราว์เซอร์ / QR Code': 'Log in in the browser / with a QR code',
  'บนมือถือ ใช้บัญชีที่ล็อกอินอยู่ในแอป LINE ได้ ครั้งแรกอาจต้องกดยินยอม':
    'On a phone you can use the account already signed in to the LINE app. The first time, LINE asks you to allow it.',
  'เราได้รับจาก LINE เพียงรหัสผู้ใช้ ชื่อ และรูปโปรไฟล์ — ไม่เห็นรหัสผ่าน เบอร์ เพื่อน หรือแชท':
    'From LINE we receive only your user id, display name and profile picture — never your password, phone number, friends or chats.',
  'หลังเข้าสู่ระบบจะมีขั้นตอนยอมรับ': 'After logging in you will be asked to accept the',
  'ยกเลิกการเข้าสู่ระบบด้วย LINE — ลองใหม่ได้ทุกเมื่อ': 'LINE login cancelled — you can try again any time.',
  'ลิงก์เข้าสู่ระบบหมดอายุ (เกิน 10 นาที) กรุณาลองใหม่': 'The login link expired (older than 10 minutes). Please try again.',
  'ตรวจสอบตัวตนกับ LINE ไม่สำเร็จ กรุณาลองใหม่': 'We could not verify your identity with LINE. Please try again.',
  'LINE ไม่ยอมรับรหัสเข้าสู่ระบบ กรุณาลองใหม่อีกครั้ง': 'LINE rejected the login code. Please try again.',
  'LINE ส่งข้อมูลกลับมาไม่ครบ กรุณาลองเข้าสู่ระบบใหม่': 'LINE sent back incomplete information. Please log in again.',
  'LINE ไม่ส่งข้อมูลตัวตนกลับมา กรุณาลองใหม่': 'LINE did not return your identity. Please try again.',
  'ระบบขัดข้องระหว่างเข้าสู่ระบบ กรุณาลองใหม่อีกสักครู่': 'Something went wrong while logging in. Please try again shortly.',
  'บัญชีนี้ถูกระงับการใช้งาน ติดต่อทีมงานหากคิดว่าเป็นความผิดพลาด':
    'This account is suspended. Contact the team if you think that is a mistake.',

  // ---------- dates ----------
  'ม.ค.': 'Jan', 'ก.พ.': 'Feb', 'มี.ค.': 'Mar', 'เม.ย.': 'Apr', 'พ.ค.': 'May', 'มิ.ย.': 'Jun',
  'ก.ค.': 'Jul', 'ส.ค.': 'Aug', 'ก.ย.': 'Sep', 'ต.ค.': 'Oct', 'พ.ย.': 'Nov', 'ธ.ค.': 'Dec',
  'จ': 'Mon', 'อ': 'Tue', 'พ': 'Wed', 'พฤ': 'Thu', 'ศ': 'Fri', 'ส': 'Sat', 'อา': 'Sun',

  // ---------- words that come from the activity registry ----------
  // The registry is data an admin can edit, so these are the words as they stand today; an id the
  // dictionary does not know keeps whatever the registry says, which is the right failure.
  'แบด': 'Badminton',
  'เทนนิส': 'Tennis',
  'โต๊ะ': 'Board games',
  'รัน': 'Run',
  'พิกเคิลบอล': 'Pickleball',
  'แบดมินตัน': 'Badminton',
  'บอร์ดเกม': 'Board games',
  'วิ่ง': 'Running',
  'สถานที่': 'Place',
  'จุดนัดพบ': 'Meeting point',
  'ร้าน': 'Café',
  // ---- co-hosts, pay-first, group invites, queue link, club invites (Nov 2026) ----
  "(ทุกคนจ่ายยอดเท่ากัน โฮสต์ดูจากสลิปว่าใครโอนแล้ว · โฮสต์เท่านั้นที่เห็น)": "(everyone pays the same amount; the host checks slips to see who paid · only the host sees it)",
  "DINKR ไม่ได้รับเงินแทนโฮสต์ ตรวจชื่อผู้รับในแอปธนาคารก่อนโอนทุกครั้ง": "DINKR does not collect money for the host. Always check the recipient's name in your banking app before you transfer.",
  "· มีคนแจ้งปัญหา ทีม DINKR กำลังตรวจสอบ ผู้เล่นที่ยังไม่จ่ายถูกแจ้งให้รอก่อน": "· someone reported a problem; the DINKR team is checking, and players who have not paid were told to wait",
  "· ยังโอนไม่ได้": "· you cannot transfer yet",
  "· โฮสต์ยืนยันยอดแล้วคุณจะได้ที่ในตี้": "· your place is confirmed once the host confirms your payment",
  "ขอให้ยืนยัน": "Ask to confirm",
  "คุณแจ้งปัญหาตี้นี้แล้ว ทีม DINKR จะตรวจสอบ": "You reported a problem with this session. The DINKR team will look into it.",
  "ค่าตี้ของคุณ": "Your session fee",
  "จองนานกว่าตี้?": "Booked longer than the session?",
  "จำนวนคอร์ท (ไม่บังคับ)": "Number of courts (optional)",
  "ดูสลิป": "View slip",
  "ตั้งค่าเก็บเงิน": "Set up payment",
  "ตั้งแต่": "From",
  "ถึง": "To",
  "ตี้นี้จ่ายก่อนได้ที่": "Pay first to get a place",
  "บันทึกหลักฐาน": "Save proof",
  "ปุ่มเตือนส่งถึงคนนั้นคนเดียว ไม่ขึ้นบนกระดานตี้ และเตือนซ้ำได้วันละครั้ง": "A reminder goes to that person only, never to the session board, and can be sent once a day.",
  "ผู้เล่นจึงจะเห็น QR และโอนได้": "before players can see the QR and transfer",
  "ยังไม่ยืนยันการจองสนาม": "Court booking not confirmed yet",
  "ยังไม่ได้โอน กดผิด": "I haven't transferred — undo",
  "ยืนยันการจองสนามก่อน": "Confirm the court booking first",
  "วันที่จอง": "Booking date",
  "สนามนี้ยังไม่มีบัญชีทีมงานใน DINKR — แนบรูปหลักฐานแทน": "This venue has no team account on DINKR yet — attach a proof picture instead.",
  "สนามไม่ได้จองจริง": "The court was not really booked",
  "สแกนแล้วยอดจะขึ้นเองครบทั้งเศษ": "Scan it and the exact amount fills itself in",
  "ส่งรายงาน": "Send report",
  "หยุดรับจ่ายชั่วคราว": "Payments paused",
  "หลักฐานดูไม่น่าเชื่อ": "The proof looks unconvincing",
  "เก็บเงิน": "Collecting",
  "เก็บเงินค่าตี้": "Collect the session fee",
  "เตือน": "Remind",
  "เป็นตัวบอกโฮสต์ว่ายอดนี้มาจากคุณ ไม่ต้องส่งสลิป": "is how the host knows this transfer is yours. No slip needed.",
  "แจ้งปัญหาการจ่ายเงิน": "Report a payment problem",
  "แจ้งโฮสต์แล้ว รอโฮสต์ยืนยัน": "Host notified — waiting for them to confirm",
  "แนบสลิปการโอน": "Attach your transfer slip",
  "แนบหลักฐานการจอง": "Attach booking proof",
  "โอน": "Transfer",
  "โฮสต์ยืนยันรับเงินแล้ว": "The host confirmed your payment",
  "โฮสต์ใหม่ (จัดตี้จบไม่ถึง 3 ครั้ง) รับจ่ายล่วงหน้าได้ไม่เกิน 8 คน คนละไม่เกิน ฿300": "New hosts (fewer than 3 completed sessions) can collect up front from at most 8 people, up to 300 baht each.",
  "โฮสต์ไม่มา / ติดต่อไม่ได้": "The host didn't show up / can't be reached",
  "ใช้หน้าการจองหรือสลิปมัดจำของตี้นี้ · ผู้เล่นในตี้จะเห็นรูปนี้ ปิดชื่อ/เบอร์โทรที่ไม่อยากให้เห็นก่อนอัปโหลด · สกรีนช็อตแชตอย่างเดียวใช้ไม่ได้": "Use the booking page or deposit slip for this session · players in the session will see it, so cover any name or phone number first · a chat screenshot alone is not accepted",
  "ให้ตรงเป๊ะรวมเศษสตางค์ — เศษ": "exactly, satang included — the",
  "ให้ระบบจำว่าใครจ่ายแล้วบ้าง และช่วยเตือนคนที่ยังค้าง — เงินโอนเข้าบัญชีคุณตรง ๆ ไม่ผ่าน DINKR": "Keep track of who has paid and nudge whoever hasn't — money goes straight to your account, never through DINKR.",
  "ให้สนามยืนยัน (เป็นโฮสต์ร่วม)": "Ask the venue to confirm (as co-host)",
  "ได้รับแล้ว": "Received",
  "ไม่พบยอด": "Not found",
  "✓ ยืนยันการจองแล้ว": "✓ Booking confirmed",
  "· ยังไม่จ่าย": "· not paid yet",
  "· รอจ่าย (ยังไม่ได้ที่)": "· waiting to pay (no place yet)",
  "· แจ้งโอนแล้ว": "· says they paid",
  "· ได้รับแล้ว": "· received",
  "QR จากแอปธนาคาร/ร้านค้า ถ้าเป็น QR จากเบอร์มือถือ ระบบจะใส่ยอดรายคนให้เอง ถ้าไม่ใช่ ผู้เล่นจะพิมพ์ยอดเอง · ไม่รับ QR ที่ผูกเลขบัตรประชาชน": "A QR from your banking or shop app. If it is linked to a mobile number, each player's amount is filled in for them; otherwise players type it in · QRs linked to a national ID are not accepted",
  "QR รับเงินของโฮสต์": "The host's payment QR",
  "ดูยอดในแอปธนาคารของคุณ เศษสตางค์ท้ายยอดบอกว่าเป็นของใคร แล้วกดได้รับแล้ว": "Check your banking app — the satang at the end of each amount tells you who sent it — then tap Received.",
  "ทีมงานสนามยืนยันการจองด้วยการเป็นโฮสต์ร่วม": "The venue's team confirmed the booking by co-hosting",
  "ทุกคนจ่ายยอดเท่ากัน เปิดดูสลิปของแต่ละคน เทียบกับแอปธนาคารของคุณ แล้วกดได้รับแล้ว": "Everyone pays the same amount. Open each person's slip, compare it with your banking app, then tap Received.",
  "บันทึกว่าได้รับแล้ว": "Marked as received",
  "ยกเลิกการยืนยันแล้ว": "Confirmation undone",
  "ยกเลิกการแจ้งแล้ว": "Undone",
  "ยอดต่อคน (บาท)": "Amount per person (baht)",
  "สนามเป็นผู้จัดตี้นี้เอง": "The venue itself is running this session",
  "หรือรูป QR รับเงินของคุณ (ไม่บังคับ)": "Or a picture of your payment QR (optional)",
  "หลักฐานการจอง": "Booking proof",
  "อ่าน QR ในรูปไม่ได้ ลองครอปให้เห็น QR ชัด ๆ": "Couldn't read a QR in that picture. Try cropping it so the QR is clear.",
  "เก็บครบแล้ว": "All collected",
  "เตือนแล้ว": "Reminder sent",
  "เบอร์พร้อมเพย์ (ไม่บังคับ)": "PromptPay number (optional)",
  "เปิดเก็บเงินแล้ว": "Payment collection is on",
  "เลือกคนจากทีมงานสนาม": "Pick someone from the venue's team",
  "เลือกรูปหลักฐานก่อน": "Choose a proof picture first",
  "แจ้งคนนั้นแล้วว่าไม่พบยอด": "They have been told the payment was not found",
  "แจ้งทีม DINKR แล้ว": "The DINKR team has been told",
  "แจ้งแล้ว · หยุดรับจ่ายชั่วคราว": "Reported · payments paused",
  "แจ้งโฮสต์แล้ว": "Host notified",
  "แนบหลักฐานแล้ว ผู้เล่นโอนได้แล้ว": "Proof attached — players can transfer now",
  "โฮสต์เป็นทีมงานของสนามนี้": "The host is on this venue's team",
  "โฮสต์แนบหลักฐานการจองไว้": "The host attached proof of the booking",
  "ใส่เป็นจำนวนเต็มบาท ทุกคนจ่ายยอดเดียวกัน": "Whole baht. Everyone pays the same amount.",
  "ใส่เป็นจำนวนเต็มบาท ระบบจะเติมเศษสตางค์ให้แต่ละคนไม่ซ้ำกัน เพื่อให้คุณดูออกว่ายอดไหนของใคร": "Whole baht. Each person gets a different satang ending so you can tell who sent which transfer.",
  "ใส่แล้วผู้เล่นจะเห็น QR ที่มียอดครบพร้อมเศษ — ใช้เบอร์มือถือเท่านั้น เราไม่เก็บเลขบัตรประชาชน": "Players will see a QR with their exact amount — mobile numbers only; we never store national ID numbers.",
  "ทุกคนจ่ายยอดเท่ากัน": "Everyone pays the same amount",
  "— ผู้เล่นแนบสลิปตอนแจ้งโอน แล้วคุณเช็กจากสลิปในหน้าตี้ · ถ้าไม่ติ๊ก ระบบจะเติมเศษสตางค์ให้แต่ละคน (฿150.01, ฿150.02…) ไม่ต้องใช้สลิป": "— players attach a slip when they say they paid, and you check it on the session page · unticked, each person gets a satang ending instead (150.01, 150.02…) and no slip is needed",
  "จ่ายก่อนได้ที่": "Pay first to get a place",
  "— คนที่กดเข้าตี้จะอยู่กลุ่มรอจ่าย จนคุณยืนยันว่าได้รับเงิน ต้องยืนยันการจองสนามก่อนผู้เล่นจึงจะโอนได้": "— people who join wait in a “waiting to pay” group until you confirm their payment. The court booking has to be confirmed before anyone can transfer.",
  "ข้อความถึงผู้เล่น (ไม่บังคับ)": "Message to players (optional)",
  "โอนแล้ว แจ้งโฮสต์": "I've paid — tell the host",
  "ถอนคำชวน": "Withdraw invitation",
  "ปฏิเสธ": "Decline",
  "ผู้เล่นธรรมดา": "A regular player",
  "ยังไม่มีคนในตี้ให้เลือก": "Nobody in the session to choose yet",
  "ยังไม่มีโฮสต์ร่วม · โฮสต์ร่วมแก้ไขตี้ จัดการรายชื่อ และยืนยันการจ่ายได้ แต่ลบหรือโอนตี้ไม่ได้": "No co-hosts yet · co-hosts can edit the session, manage the roster and confirm payments, but cannot delete or hand it over",
  "หลังโอนแล้ว คุณจะเป็น": "After the handover you will be",
  "เพิ่มโฮสต์ร่วม": "Add a co-host",
  "เลือก": "Choose",
  "โอนเจ้าของตี้": "Hand over the session",
  "โฮสต์ร่วม": "Co-host",
  "กดรับ = คุณจะช่วยดูแลตี้นี้ที่สนามด้วย": "Accepting means you will help run this session at the venue.",
  "ปฏิเสธแล้ว": "Declined",
  "รอกดรับ": "waiting to accept",
  "รอรับโอนตี้": "handover pending",
  "รับตี้เรียบร้อย": "The session is yours now",
  "รับเป็นเจ้าของ": "Accept ownership",
  "รับเป็นโฮสต์ร่วม": "Accept co-hosting",
  "ลาออกจากโฮสต์ร่วม": "Step down as co-host",
  "ส่งคำชวนแล้ว รอเขากดรับ": "Invitation sent — waiting for them to accept",
  "เป็นโฮสต์ร่วมแล้ว": "You're a co-host now",
  "เรียบร้อย": "Done",
  "เลือกคนในตี้ เขาต้องกดรับก่อนจึงจะเป็นโฮสต์ร่วม": "Pick someone in the session. They have to accept before they become a co-host.",
  "เลือกคนในตี้ที่จะรับเป็นเจ้าของ เขาต้องกดรับก่อน ตี้จึงจะโอนไป": "Pick someone in the session to take it over. Nothing moves until they accept.",
  "เอาออก": "Remove",
  "ชวนทั้งคลับแจ้งในเว็บและ LINE (สำหรับคนที่เปิดรับ) · ชวนทั้งเว็บแจ้งในเว็บอย่างเดียว · ใครปิดรับคำชวนแบบกลุ่มไว้จะไม่ได้รับ": "A club invite arrives in the app and on LINE (for people who opted in) · a site-wide invite is in-app only · anyone who turned group invites off won't get either",
  "ชวนแบบกลุ่ม": "Group invites",
  "ครบโควตาวันนี้": "Daily limit reached",
  "ชวนทั้งคลับ": "Invite the club",
  "ชวนทั้งเว็บ": "Invite everyone",
  "ชวนแล้ว": "Invited",
  "ประกาศชวนทั้งเว็บแล้ว": "Invitation posted to everyone",
  "ค้นหาชื่อหรือ @handle": "Search a name or @handle",
  "✓ เลือกแล้ว": "✓ Selected",
  "รอจ่าย · ออกจากตี้": "Waiting to pay · leave",
  "รอจ่าย": "Waiting to pay",
  "กดเข้าตี้แล้วแต่ยังไม่ได้ที่ จะขึ้นไปอยู่ในรายชื่อเมื่อโฮสต์ยืนยันว่าได้รับเงิน · ไม่นับในจำนวนคน": "Joined but no place yet — they move onto the roster once the host confirms their payment · not counted",
  "เปิด Shuffle คิว": "Open the shuffle queue",
  "จัดคนลงคอร์ทอัตโนมัติ · ดึงรายชื่อตี้นี้ไปให้เอง": "Puts people on court automatically · brings this session's roster with it",
  "จัดในนาม": "Hosted as",
  "ตัวเอง (ผู้เล่น)": "Myself (player)",
  "ในนามสนาม: บัญชีสนามเป็นโฮสต์ คุณเป็นโฮสต์ร่วม และทีมงานสนามดูแลตี้นี้ได้ด้วย": "As the venue: the venue's account hosts it, you are a co-host, and the venue's team can manage it too",
  "คลับนี้ชวนคุณเข้าร่วม": "This club invited you to join",
  "รับคำชวน": "Accept invitation",
  "เข้าร่วมคลับแล้ว": "You joined the club",
  "ปฏิเสธคำชวนแล้ว": "Invitation declined",
  "ชวนคนเข้าคลับ": "Invite people to the club",
  "ค้นหาใครก็ได้ในเว็บ เขาจะได้แจ้งเตือนและต้องกดรับก่อนจึงจะเป็นสมาชิก · คนหนึ่งอยู่ได้หลายคลับ": "Search anyone on DINKR. They get a notification and become a member only after accepting · a person can be in several clubs",
  "ชวนสมาชิกก๊วน": "Invite your crew",
  "แตะเพื่อเลือก/ไม่เลือก · เขาต้องกดรับคำชวนก่อนจึงจะเป็นสมาชิก": "Tap to select or deselect · they become members only after accepting",
  "คำชวนเข้าคลับ": "Club invitations",
  "ชวนคุณเข้าร่วม · เปิดหน้าคลับเพื่อกดรับ": "Invited you · open the club page to accept",
  "หยุดรับจ่ายชั่วคราว มีคนแจ้งปัญหา ทีม DINKR กำลังตรวจสอบ อย่าเพิ่งโอน": "Payments are paused — someone reported a problem and the DINKR team is checking. Don't transfer yet.",
  "ยังโอนไม่ได้ โฮสต์ต้องยืนยันการจองสนามก่อน": "You can't transfer yet — the host has to confirm the court booking first.",
  "ตี้เต็มแล้ว อย่าเพิ่งโอน": "The session is full — don't transfer.",
  "โฮสต์นี้ถูกระงับการเก็บเงินล่วงหน้า อย่าโอน": "This host can no longer collect up front — don't transfer.",
  "โฮสต์ใหม่รับจ่ายล่วงหน้าครบจำนวนแล้ว อย่าเพิ่งโอน": "This new host has reached their limit for up-front payments — don't transfer yet.",
  "ทุกคนจ่ายยอดเท่ากัน ผู้เล่นแนบสลิปตอนแจ้งโอน แล้วคุณเช็กสลิปของแต่ละคนได้ในหน้าตี้": "Everyone pays the same amount. Players attach a slip when they say they paid, and you check each slip on the session page.",
  "ใส่แล้วผู้เล่นจะเห็น QR ที่มียอดอยู่แล้ว — ใช้เบอร์มือถือเท่านั้น เราไม่เก็บเลขบัตรประชาชน": "Players will see a QR with the amount already in it — mobile numbers only; we never store national ID numbers.",
  "QR จากแอปธนาคาร/ร้านค้า ถ้าเป็น QR จากเบอร์มือถือ ระบบจะใส่ยอดให้เอง ถ้าไม่ใช่ ผู้เล่นจะพิมพ์ยอดเอง · ไม่รับ QR ที่ผูกเลขบัตรประชาชน": "A QR from your banking or shop app. If it is linked to a mobile number, the amount is filled in for players; otherwise they type it in · QRs linked to a national ID are not accepted",
  "สแกนแล้วยอดจะขึ้นเอง": "Scan it and the amount fills itself in",
  "พาเดล": "Padel",
  "นัดดูหนัง": "Movies",
  "ดูหนัง": "Movies",
  "เปิดตี้พาเดล": "Open a padel session",
  "ชวนดูหนัง": "Plan a movie night",
};
